import { Address, BidderGroup, User } from "@prisma/client";
import { Flex } from "@radix-ui/themes";
import { ColumnDef, Row } from "@tanstack/react-table";
import { DataTableColumnHeader } from "../components/table/DataTableColumnHeader";
import { format } from "date-fns";
import { cn, formatAddress } from "@/lib/utils";
import { EMPTY_SENTINEL_VALUE } from "@/lib/constants";
import GroupSelector from "./GroupSelector";
import { DataTableRowActions } from "./DataTableRowActions";
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip";
import { dateBetweenFilter } from "@/lib/tanstack/dateFilter";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";

export type BidderTableData = User & {
    Address: Address[] | null;
    bidderGroup: BidderGroup | null;
}

const bidderSearchFn = <TData extends BidderTableData>(
    row: Row<TData>,
    id: string,
    filterValue: any
) => {
    if (filterValue == EMPTY_SENTINEL_VALUE) {
        const value = row.getValue(id);
        return Boolean(!value);
    }

    if (!filterValue) return true;

    const firstAddress = row.original.Address?.[0] ?? null;

    const searchRows = [
        row.original.name + " " + row.original.lastName,
        row.original.email,
        row.original.phoneNumber,
        firstAddress?.address1,
        firstAddress?.address2,
        firstAddress?.city,
        firstAddress?.region,
        firstAddress?.postalCode,
    ];

    return (
        searchRows
            .some(field => field?.toLowerCase().includes(filterValue.toLowerCase()))
    );
};

const FlaggedTooltip = ({ children }: { children: React.ReactNode }) => {
    return (
        <Tooltip delayDuration={150}>
            <TooltipTrigger className="w-fit text-left cursor-pointer" asChild>
                {children}
            </TooltipTrigger>
            <TooltipContent side="right" className="ml-2">
                This bidder has been flagged.
            </TooltipContent>
        </Tooltip>
    );
};

const IncompleteTooltip = ({ children }: { children: React.ReactNode }) => {
    return (
        <Tooltip delayDuration={150}>
            <TooltipTrigger className="w-full text-left cursor-pointer" asChild>
                {children}
            </TooltipTrigger>
            <TooltipContent side="right" className="ml-2 w-60">
                This bidder has an incomplete profile. They will not be able to bid until their profile is complete.
            </TooltipContent>
        </Tooltip>
    );
};

export const columns: ColumnDef<BidderTableData>[] = [
    {
        accessorKey: "id",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="ID" />
        ),
        cell: ({ row }) => {
            const { original: { id } } = row;
            return <span>{id}</span>;
        },
    },
    {
        accessorKey: "name",
        size: 100,
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Name" />
        ),
        cell: ({ row }) => {
            const {
                original: { name, lastName, flagged },
            } = row;

            const isIncomplete = !row.original.profileComplete;

            const nameAndIconDisplay: React.ReactNode = (
                <Flex justify="start" align="center" className={cn(
                    'space-x-2 w-min',
                    flagged && 'text-red-500 font-medium',
                )}>
                    <span className="w-fit whitespace-nowrap">
                        {name || ''} {lastName || ''}
                    </span>
                    {isIncomplete &&
                        <ExclamationTriangleIcon className="w-3 h-3 translate-y-[1px]" />
                    }
                </Flex>
            );

            if (flagged) {
                return (
                    <FlaggedTooltip>
                        {nameAndIconDisplay}
                    </FlaggedTooltip>
                );
            } else if (isIncomplete) {
                return (
                    <IncompleteTooltip>
                        {nameAndIconDisplay}
                    </IncompleteTooltip>
                );
            }

            return nameAndIconDisplay;
        },
        filterFn: bidderSearchFn
    },
    {
        accessorKey: "email",
        size: 200,
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Email" />
        ),
        cell: ({ row }) => {
            const { original: { email }, } = row;
            return (
                <Flex justify="start" className="space-x-2">
                    <span className="font-medium cursor-pointer">
                        {email}
                    </span>
                </Flex>
            );
        },
    },
    {
        accessorKey: "phoneNumber",
        size: 40,
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Phone" />
        ),
        cell: ({ row }) => {
            const { original: { phoneNumber } } = row;
            return <span>{phoneNumber || '-'}</span>;
        },
    },
    {
        accessorKey: "Address",
        size: 300, // Add default width
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Address" />
        ),
        cell: ({ row }) => {
            const { original: { Address } } = row;
            const firstAddress = Address?.[0] ?? null;
            const formattedAddress = formatAddress(firstAddress);
            return (
                <Flex justify="start" className="space-x-2 w-fit">
                    <span className="w-fit">{formattedAddress || '-'}</span>
                </Flex>
            )
        },
    },
    {
        accessorKey: "bidderGroup.name",
        maxSize: 50,
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Group" />
        ),
        cell: ({ row }) => {
            const { original: { id, bidderGroup } } = row;
            return <GroupSelector userId={id} currentGroup={bidderGroup} />;
        },
        sortingFn: (
            rowA,
            rowB
        ) => {
            const groupNameA = rowA.original.bidderGroup?.name ?? '';
            const groupNameB = rowB.original.bidderGroup?.name ?? '';
            return groupNameA.localeCompare(groupNameB);
        },
        filterFn: (row, id, value) => {
            return value.includes(row.getValue(id))
        },
    },
    {
        accessorKey: "createdAt",
        minSize: 80,
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Created At" />
        ),
        cell: ({ row }) => {
            const { original: { createdAt } } = row;
            return <span suppressHydrationWarning>{createdAt ? format(new Date(createdAt), 'PPp') : '-'}</span>;
        },
        filterFn: (row, id, value) => dateBetweenFilter(row, id, value, () => { })
    }, {
        id: "actions",
        size: 20,
        cell: ({ row }) => <DataTableRowActions row={row} />,
    }
]